import { Resource } from 'constants/resources';

import { useMemo } from 'react';

import { useGetMany } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Chip } from 'shared/mui/Chips';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { FilterChipLabel } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/FilterChipLabel/FilterChipLabel';
import { PopoverChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/PopoverChip/PopoverChip';
import { getLimitTagsText } from 'utils/getLimitTagsText';

export type FilterEnumMultiselectChipProps = {
  label: string;
  resource: Resource;
  name: string;
  limitTags?: number;
  children: React.ReactNode;
  onDelete?: () => void;
};

export const FilterEnumMultiselectChip: React.FC<FilterEnumMultiselectChipProps> = ({
  resource,
  children,
  limitTags,
  name,
  label,
  onDelete,
}) => {
  const { getValues, setValue } = useFormContext();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();

  const filterValues = appliedFilters?.[name] || [];

  const { data = [], isLoading } = useGetMany(
    resource,
    { ids: filterValues.map((value: string) => `admin/${resource}/${value}`) },
    { enabled: Boolean(filterValues.length) },
  );

  const tags = useMemo(() => {
    const names = data.map((item) => item.description).filter(Boolean);

    return getLimitTagsText(names, limitTags);
  }, [data, limitTags]);

  if (!filterValues.length) {
    return <></>;
  }

  function onApply() {
    handleClose();
    setAppliedFilter(name, getValues(name));
  }

  function onReset() {
    setAppliedFilter(name, appliedFilters?.[name]);
    setValue(name, appliedFilters?.[name]);
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <PopoverChip
        anchorEl={anchorEl}
        onApply={onApply}
        onClose={handleClose}
        onReset={onReset}
        open={open}
      >
        {children}
      </PopoverChip>
      <Chip
        label={<FilterChipLabel isLoading={isLoading} label={label} value={tags} />}
        onClick={handleClick}
        onDelete={() => {
          onDelete && onDelete();
          setValue(name, []);
          setAppliedFilter(name, []);
        }}
        size="medium"
      />
    </>
  );
};
